import { Divider } from 'antd'
import { LucideIcon } from 'components/LucideIcon'
import {
  useTitle,
  useTypeDisplayName,
  useTypeMetadata,
} from 'features/dynamicOntology'
import React from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoEntry } from 'types'

import { ObjectBreadcrumbs } from './ObjectBreadcrumbs'

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 0 0.5rem 0;
`

const ObjectName = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  word-break: break-word;
  color: rgba(
    229,
    229,
    229,
    1
  ); // Default text color for Forecast, but not in CantileverTheme
`

const ObjectMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ObjectTypeContainer = styled.div<{ $typeColor: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0 0;
`

const ObjectType = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${CantileverTheme.colors.gray['700']};
  text-transform: uppercase;
  letter-spacing: 0.075rem;
`

const ObjectTypeIcon = styled.div<{ $typeColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${(props) => props.$typeColor};
  color: white;
  border-radius: 0.375rem;
`

const ObjectHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

interface ObjectViewTitleProps {
  entry: GeckoEntry
}

export const ObjectViewTitle: React.FC<ObjectViewTitleProps> = ({ entry }) => {
  const title = useTitle(entry)
  const typeName = useTypeDisplayName(entry.type)
  const { iconName, typeColor } = useTypeMetadata(entry.type)

  return (
    <TitleContainer>
      <ObjectBreadcrumbs entry={entry} />

      <ObjectMetadata>
        <ObjectHeaderContainer>
          {iconName && (
            <ObjectTypeIcon $typeColor={typeColor}>
              <LucideIcon name={iconName} size={20} />
            </ObjectTypeIcon>
          )}
          <ObjectName>{title}</ObjectName>
        </ObjectHeaderContainer>
        <ObjectTypeContainer $typeColor={typeColor}>
          <ObjectType>{typeName}</ObjectType>

          <Divider style={{ height: '1.5rem' }} orientation='vertical' />
          <ObjectType>{entry.id}</ObjectType>
        </ObjectTypeContainer>
      </ObjectMetadata>
    </TitleContainer>
  )
}
